import { IResult } from '../models/HttpResult';
import { DocumentationPage } from '../models/Entities/HelpDocumentation/DocumentationPage';
import { DocumentationFeedback } from '../models/Entities/HelpDocumentation/DocumentationFeedback';
import { buildHeaders, handleApiError } from './commons/CommonService.Functions';

/**
 * El gateway publica la API de ayuda bajo `/help-documentation` y adentro reescribe
 * a `/api/v1`. Las imágenes incrustadas en el contenido, en cambio, vienen con la
 * ruta propia de la API (`/api/v1/documentation/...`), así que hay que rearmarlas
 * contra esta base: ver `readMediaReference` en las funciones del componente.
 */
const BaseURL =
    (process.env.REACT_APP_BILLER_API_GATEWAY || '').replace(/\/$/, '') +
    '/help-documentation/documentation';

/** Página de ayuda de un código de enlace (GET .../documentation/{linkCode}). */
export const GetDocumentationPage = async (
    linkCode: string,
    token: string
): Promise<DocumentationPage> => {
    const url = `${BaseURL}/${encodeURIComponent(linkCode)}`;

    try {
        const response = await fetch(url, { headers: buildHeaders(token) });
        if (!response.ok) throw response;

        const data: IResult<DocumentationPage> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'GetDocumentationPage');
    }
};

/** Dirección de una imagen del contenido, ya apuntada al gateway. */
export const BuildDocumentationMediaUrl = (linkCode: string, mediaId: string): string =>
    `${BaseURL}/${encodeURIComponent(linkCode)}/media/${encodeURIComponent(mediaId)}`;

/**
 * Bytes de una imagen incrustada en una página.
 *
 * Se baja con fetch y no se deja en el `src` porque el endpoint exige el token
 * como cualquier otro, y un `<img src>` no puede mandarlo: el navegador manda
 * cookies, no encabezados elegidos. La alternativa —el token en la URL— lo dejaría
 * escrito en el historial, en el `Referer` y en los logs del servidor.
 */
export const GetDocumentationMedia = async (
    linkCode: string,
    mediaId: string,
    token: string
): Promise<Blob> => {
    const url = BuildDocumentationMediaUrl(linkCode, mediaId);

    try {
        const response = await fetch(url, {
            headers: buildHeaders(token, { Accept: 'image/*' }),
        });
        if (!response.ok) throw response;

        return await response.blob();
    } catch (error: any) {
        throw await handleApiError(error, 'GetDocumentationMedia');
    }
};

/**
 * Registra lo que el lector opinó de una página
 * (POST .../documentation/{linkCode}/feedback).
 *
 * Quien responde dos veces sobre la misma página reemplaza su propia respuesta en
 * vez de sumar una segunda, así que volver a enviar con más detalle es la forma
 * prevista de completar una respuesta, no un duplicado.
 */
export const SubmitDocumentationFeedback = async (
    linkCode: string,
    feedback: DocumentationFeedback,
    token: string
): Promise<string> => {
    const url = `${BaseURL}/${encodeURIComponent(linkCode)}/feedback`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(token),
            body: JSON.stringify(feedback),
        });
        if (!response.ok) throw response;

        const data: IResult<string> = await response.json();
        return data.data;
    } catch (error: any) {
        throw await handleApiError(error, 'SubmitDocumentationFeedback');
    }
};
